import { useEffect, useRef } from 'react';

import { audioContext } from './call-tones';

import type { WhatsAppCallStreamEvent } from './use-whatsapp-call';
import type { WhatsAppCallLines } from './whatsapp-calls.api';

import { env } from '@/env';
import { getAccessToken } from '@/stores/auth-store';

export interface WhatsAppStreamEvent {
  conversationId: string;
  direction: 'IN' | 'OUT';
  event: 'message' | 'status';
}

/**
 * Live WhatsApp chat activity over the notifications SSE endpoint
 * (`event: whatsapp` frames, broadcast to every connected admin).
 *
 * The payload is only an invalidation hint - the widget refetches its
 * scope-filtered queries on receipt, so an agent never learns anything the
 * inbox API wouldn't already show them. When the stream is down (Redis
 * offline, proxy hiccup) EventSource auto-reconnects and the widget's
 * polling keeps working - this only makes it instant.
 */
export const useWhatsAppStream = ({
  enabled,
  onEvent,
  onCallEvent,
  onLinesEvent,
}: {
  enabled: boolean;
  onEvent: (payload: WhatsAppStreamEvent) => void;
  /** `event: whatsapp-call` frames - call ringing / answered / ended. */
  onCallEvent?: (payload: WhatsAppCallStreamEvent) => void;
  /** `event: whatsapp-call-lines` frames - how many calling lines are busy. */
  onLinesEvent?: (payload: WhatsAppCallLines) => void;
}): void => {
  const onEventRef = useRef(onEvent);
  const onCallEventRef = useRef(onCallEvent);
  const onLinesEventRef = useRef(onLinesEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
    onCallEventRef.current = onCallEvent;
    onLinesEventRef.current = onLinesEvent;
  }, [onEvent, onCallEvent, onLinesEvent]);

  useEffect(() => {
    if (!enabled) return;
    const token = getAccessToken();
    if (!token) return;

    const url = `${env.VITE_API_BASE_URL}/notifications/me/stream?token=${encodeURIComponent(token)}`;
    const source = new EventSource(url);
    source.addEventListener('whatsapp', (e) => {
      try {
        onEventRef.current(JSON.parse((e as MessageEvent).data) as WhatsAppStreamEvent);
      } catch {
        // Malformed hint - the polling fallback will catch the change.
      }
    });
    source.addEventListener('whatsapp-call', (e) => {
      try {
        onCallEventRef.current?.(JSON.parse((e as MessageEvent).data) as WhatsAppCallStreamEvent);
      } catch {
        // Malformed frame - the softphone's own status poll covers live calls.
      }
    });
    source.addEventListener('whatsapp-call-lines', (e) => {
      try {
        onLinesEventRef.current?.(JSON.parse((e as MessageEvent).data) as WhatsAppCallLines);
      } catch {
        // Malformed frame - the next call attempt still reports a busy pool.
      }
    });
    // 'error' needs no handler: EventSource reconnects on its own, and a
    // hard 503 (SSE disabled) simply leaves polling in charge.
    return () => source.close();
  }, [enabled]);
};

/**
 * WhatsApp-ish two-tone pop for an incoming message. No asset needed. Uses
 * the call-tones context, which installCallAudioUnlock resumes on the first
 * tap / key - a context created here on its own stayed suspended until the
 * browser saw a gesture, so the first chimes were silent.
 */
export const playIncomingChime = (): void => {
  try {
    const ctx = audioContext();
    if (!ctx) return;
    const now = ctx.currentTime;
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.12, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.18);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.2);
    });
  } catch {
    // Autoplay blocked before first interaction - silent is fine.
  }
};
